import { useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Eyebrow, MaskText } from "@/components/motion";
import { track } from "@/lib/pixel";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const Newsletter = () => {
  const ref = useRef(null);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  const submit = async (e) => {
    e.preventDefault();
    if (!BACKEND) {
      toast.error("Sign-up is unavailable right now.");
      return;
    }
    setState("loading");
    try {
      const res = await axios.post(`${BACKEND}/api/subscribe`, { email, source: "beauty-edit-landing" });
      track("Lead", { content_name: "newsletter" });
      toast.success(res.data?.status === "exists" ? "You're already on the list." : "You're on the list. Watch your inbox.");
      setState("done");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.status === 422 ? "Please enter a valid email address." : "Something went wrong. Please try again.");
      setState("idle");
    }
  };

  return (
    <section id="newsletter" ref={ref} data-testid="newsletter-section" className="relative overflow-hidden py-28 lg:py-40">
      <motion.img style={{ y, scale: 1.2 }} src="/media/celestial-case.webp" alt="" className="absolute inset-0 h-full w-full object-cover object-right opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="max-w-xl">
          <Eyebrow>11 — The list</Eyebrow>
          <h2 className="mt-5 font-display text-5xl font-light leading-[0.95] text-cream sm:text-6xl lg:text-7xl">
            <MaskText lines={["First to know.", <em key="e" className="italic text-champagne">First to glow.</em>]} />
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-stone sm:text-base">New launches, restocks and offers from the Beauty Edit, sent to your inbox before anyone else.</p>
          <form onSubmit={submit} className="mt-10 flex flex-col gap-3 sm:flex-row" data-testid="newsletter-form">
            <label htmlFor="nl-email" className="sr-only">Email address</label>
            <input
              id="nl-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="newsletter-email-input"
              className="h-14 flex-1 rounded-full border border-white/20 bg-ink/60 px-6 text-cream placeholder:text-stone backdrop-blur-md focus:border-champagne focus:outline-none"
            />
            <button type="submit" disabled={state === "loading"} data-testid="newsletter-submit-btn" className="btn-gold h-14 justify-center">
              {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : state === "done" ? <Check className="h-4 w-4" /> : null}
              {state === "done" ? "Subscribed" : "Join the list"}
              {state === "idle" && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
